/**
 * abilityText — an ability's words and numbers, compiled from content
 * (plan 29 F2, lane BS 2026-10-11).
 *
 * Before: the hotbar tooltip read `ability.damage`, which no registry entry
 * carried, so no tooltip, spellbook page or trainer entry showed a damage or
 * heal number; the 18 TOML-built abilities had no description at all; the
 * trainer said "Train this level N … ability."; and hand-written module
 * prose drifted from what the abilities do (plan 29 bug 7).
 *
 * Now there is one source for each:
 *  - the DESCRIPTION is the TOML `description` (prose, no numbers);
 *  - the STAT LINE is computed from the same TOML at the player's level with
 *    the formulas the server uses: damage from `abilityBaseDamage` (the base
 *    `calculateDamage` starts from) ±20% variance; heals from
 *    `abilityHealAmount` (HealCastDelivery), HoTs spread as the server ticks
 *    them, totem pulses from `healAuraSpec`; reach from `resolveMeleeCone`
 *    for melee swings (what the swing draws and hits) or TOML `range`;
 *    radius, duration, cast time, cooldown and mana straight from the TOML.
 *
 * The hotbar tooltip, the spellbook and the trainer all render this.
 */
import { getAbilityData, type AbilityDataDef } from './AbilityDataAdapter';
import { abilityBaseDamage, DAMAGE_VARIANCE_FRACTION } from '../logic/CombatLogic';
import { abilityHealAmount, healShape, isAreaHeal } from '../combat/friendlyCasts';
import { healAuraSpec } from '../combat/healAuras';
import { resolveMeleeCone } from '../logic/MeleeConeProfile';
import { periodicStatusSchedule } from '../combat/periodicAbilityStatus';

/** The caster the numbers are computed for (client character or server Player). */
export interface AbilityTextCaster {
  level?: number;
  /** Client `class` or server `heroClass`. */
  class?: string;
  heroClass?: string;
  attack?: number;
  magicAttack?: number;
  intelligence?: number;
  maxHealth?: number;
  maxMana?: number;
  stats?: { attack?: number; magicAttack?: number; intelligence?: number; maxHp?: number; maxMp?: number };
}

export type AbilityStatKey = 'damage' | 'heal' | 'reach' | 'radius' | 'duration' | 'cast' | 'cooldown' | 'mana'
  | 'absorb' | 'immune' | 'reduction' | 'stat' | 'slow' | 'root' | 'output' | 'periodic' | 'exposure' | 'health_cost' | 'mana_restore';

export interface AbilityStat {
  key: AbilityStatKey;
  label: string;
  value: string;
}

// The damage predicate is shared with the compile gate (import-free module).
export { abilityDealsDamage } from './playerAbilityRules';
import { abilityDealsDamage } from './playerAbilityRules';

/** The authored description, or '' when the TOML has none. */
export function abilityDescription(abilityId: string): string {
  return getAbilityData(abilityId)?.description ?? '';
}

function casterNumbers(caster: AbilityTextCaster) {
  return {
    level: Math.max(1, caster.level ?? 1),
    heroClass: caster.heroClass ?? caster.class ?? 'warrior',
    attack: caster.stats?.attack ?? caster.attack ?? 0,
    magicAttack: caster.stats?.magicAttack ?? caster.magicAttack ?? 0,
    intelligence: caster.stats?.intelligence ?? caster.intelligence ?? 0,
    maxHealth: caster.stats?.maxHp ?? caster.maxHealth ?? 100,
    maxMana: caster.maxMana ?? caster.stats?.maxMp ?? 100,
  };
}

/** Damage per hit at this caster's level, before the target ([min, max]). */
export function abilityDamageRange(abilityId: string, caster: AbilityTextCaster): { min: number; max: number } | null {
  const data = getAbilityData(abilityId);
  if (!abilityDealsDamage(data)) return null;
  const c = casterNumbers(caster);
  const base = abilityBaseDamage({ level: c.level, heroClass: c.heroClass, attack: c.attack, magicAttack: c.magicAttack }, abilityId);
  return {
    min: Math.max(1, Math.round(base * (1 - DAMAGE_VARIANCE_FRACTION))),
    max: Math.max(1, Math.round(base * (1 + DAMAGE_VARIANCE_FRACTION))),
  };
}

const seconds = (ms: number): string => (ms >= 120000
  ? `${Number((ms / 60000).toFixed(1))} min`
  : `${Number((ms / 1000).toFixed(1))} s`);

/** The heal clause for a heal ability, as the server delivers it. */
function healStat(data: AbilityDataDef, caster: AbilityTextCaster): AbilityStat | null {
  const c = casterNumbers(caster);
  const aura = healAuraSpec(data.id);
  if (aura) {
    const per = aura.amount({ intelligence: c.intelligence, magicAttack: c.magicAttack });
    return { key: 'heal', label: 'Heal', value: `${per} every ${seconds(aura.tickRateMs)}` };
  }
  const shape = healShape(data);
  if (shape === 'none') return null;
  const amount = abilityHealAmount(data, { maxHealth: c.maxHealth, intelligence: c.intelligence });
  if (shape === 'hot') {
    const ticks = Math.max(1, Math.floor((data.durationMs ?? 0) / 1000));
    return { key: 'heal', label: 'Heal', value: `${Math.max(1, Math.round(amount / ticks)) * ticks} over ${seconds(data.durationMs ?? 0)}` };
  }
  if (shape === 'area') return { key: 'heal', label: 'Heal', value: `${amount} to each friend` };
  return { key: 'heal', label: 'Heal', value: String(amount) };
}

const SCHOOL_WORD = (schools?: string[]): string => {
  if (!schools || schools.length === 0) return 'all damage';
  const magic = ['fire', 'frost', 'arcane', 'shadow', 'holy', 'nature', 'chaos'];
  if (magic.every((m) => schools.includes(m)) && !schools.includes('physical')) return 'magic';
  return schools.join(', ');
};

/** Status stat keys are code identifiers; hp/mp bonuses raise resource maxima. */
export function abilityStatLabel(stat: string): string {
  const words = stat === 'hp' ? 'max health' : stat === 'mp' ? 'max mana'
    : stat.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Lane BR's server statuses (TOML `[[<id>.status]]`, plan 29 F1/F1b), as the
 * server applies them: absorb = flat + share of the recipient's max HP,
 * reduction/slow as a percentage, immunity by school, stat buffs, roots.
 */
function statusStats(data: AbilityDataDef, maxHealth: number): AbilityStat[] {
  const out: AbilityStat[] = [];
  for (const st of data.statuses ?? []) {
    const lasts = ` for ${seconds(st.durationMs)}`;
    if (st.kind === 'absorb') {
      const pts = Math.round((st.amount ?? 0) + (st.maxHpPct ?? 0) * maxHealth);
      out.push({ key: 'absorb', label: 'Absorbs', value: `${pts}${lasts}` });
    } else if (st.kind === 'immunity') {
      out.push({ key: 'immune', label: 'Immune', value: `${SCHOOL_WORD(st.schools as string[] | undefined)}${lasts}` });
    } else if (st.kind === 'reduction') {
      const on = st.schools && st.schools.length ? ` ${SCHOOL_WORD(st.schools as string[])}` : '';
      out.push({ key: 'reduction', label: 'Damage taken', value: `−${Math.round((st.percent ?? 0) * 100)}%${on}${lasts}` });
    } else if (st.kind === 'stat_buff') {
      for (const [stat, v] of Object.entries(st.stats ?? {})) {
        out.push({ key: 'stat', label: abilityStatLabel(stat), value: `+${v}${lasts}` });
      }
    } else if (st.kind === 'outgoing_damage') {
      out.push({ key: 'output', label: 'Damage dealt', value: `+${Math.round((st.percent ?? 0) * 100)}%${lasts}` });
    } else if (st.kind === 'incoming_damage') {
      out.push({ key: 'exposure', label: `${SCHOOL_WORD(st.schools as string[] | undefined) === 'magic' ? 'Magic d' : 'D'}amage taken`,
        value: `+${Math.round((st.percent ?? 0) * 100)}%${lasts}` });
    } else if (st.kind === 'slow') {
      out.push({ key: 'slow', label: 'Slow', value: `${Math.round((st.percent ?? 0) * 100)}%${lasts}` });
    } else if (st.kind === 'root') {
      out.push({ key: 'root', label: 'Root', value: seconds(st.durationMs) });
    } else if (st.kind === 'periodic_damage') {
      const schedule = periodicStatusSchedule([st]);
      if (!schedule) continue;
      const { amounts, intervalMs, durationMs } = schedule;
      const total = amounts.reduce((sum, amount) => sum + amount, 0);
      const first = amounts[0], uniform = amounts.every(amount => amount === first);
      const prefix = `${first} ${data.school} every ${seconds(intervalMs)}`;
      const value = uniform ? `${prefix} × ${amounts.length} (${total} over ${seconds(durationMs)})`
        : amounts.slice(0, -1).every(amount => amount === first)
          ? `${prefix} × ${amounts.length - 1}; ${amounts[amounts.length - 1]} at ${seconds(durationMs)} (${total} total)`
          : `${amounts.map((amount, index) => `${amount} at ${seconds((index + 1) * intervalMs)}`).join('; ')} (${total} ${data.school} total)`;
      out.push({ key: 'periodic', label: 'Periodic damage', value });
    }
  }
  return out;
}

/**
 * These authored areas land on the caster and already expose their full extent
 * as a TOML radius. `target = "aoe"` alone cannot identify them: it also names
 * Cleave's swing, Howl's cone, forward-placed fields and projectile volleys.
 * Placement currently lives in client builders, so keep this bounded list
 * checked against the cast-geometry snapshot instead of importing game code.
 */
const CASTER_CENTRED_AREAS = new Set([
  'blade_fury', 'bladestorm', 'divine_storm', 'ground_slam', 'hurricane', 'whirlwind', 'consecration',
]);

/** Target reach: a melee swing's real cone or TOML range; centred areas have a radius. */
export function abilityReachPx(data: AbilityDataDef, heroClass: string): number | null {
  if (isAreaHeal(data) || (data.target === 'aoe' && CASTER_CENTRED_AREAS.has(data.id))) return null;
  if (data.tags?.includes('melee')) return Math.round(resolveMeleeCone(data.id, heroClass).rangePx);
  if (data.target === 'self' || data.target === 'ground') return null;
  return data.range && data.range > 0 ? Math.round(data.range) : null;
}

/** The stat line: every number the player needs, computed from content. */
export function abilityStatLine(abilityId: string, caster: AbilityTextCaster): AbilityStat[] {
  const data = getAbilityData(abilityId);
  if (!data) return [];
  const c = casterNumbers(caster);
  const out: AbilityStat[] = [];
  const dmg = abilityDamageRange(abilityId, caster);
  if (dmg) out.push({ key: 'damage', label: 'Damage', value: dmg.min === dmg.max ? String(dmg.min) : `${dmg.min}–${dmg.max}` });
  if (data.abilityType === 'heal') {
    const heal = healStat(data, caster);
    if (heal) out.push(heal);
  }
  out.push(...statusStats(data, c.maxHealth));
  if (data.id === 'life_tap') {
    out.push({ key: 'health_cost', label: 'Health cost', value: `${Math.floor(c.maxHealth * (data.selfHpCostPct ?? 0))} (${Math.round((data.selfHpCostPct ?? 0) * 100)}% max health, nonlethal)` });
    out.push({ key: 'mana_restore', label: 'Mana restored', value: `up to ${Math.floor(c.maxMana * (data.restoreManaPct ?? 0))} (${Math.round((data.restoreManaPct ?? 0) * 100)}% max mana)` });
  }
  const reach = abilityReachPx(data, c.heroClass);
  if (reach) out.push({ key: 'reach', label: 'Reach', value: `${reach} px` });
  if (data.radius && data.radius > 0) out.push({ key: 'radius', label: 'Radius', value: `${Math.round(data.radius)} px` });
  if (data.durationMs && data.durationMs > 0) out.push({ key: 'duration', label: 'Lasts', value: seconds(data.durationMs) });
  if (data.windUpMs && data.windUpMs > 0) {
    out.push({ key: 'cast', label: data.mechanicType === 'channel' ? 'Channel' : 'Cast', value: seconds(data.windUpMs) });
  }
  if (data.cooldownMs > 0) out.push({ key: 'cooldown', label: 'Cooldown', value: seconds(data.cooldownMs) });
  if (data.manaCost > 0) out.push({ key: 'mana', label: 'Mana', value: String(data.manaCost) });
  return out;
}

/** The stat line as short strings ("Damage 18–27"), for compact surfaces. */
export function abilityStatStrings(abilityId: string, caster: AbilityTextCaster): string[] {
  return abilityStatLine(abilityId, caster).map((s) => `${s.label} ${s.value}`);
}
